/*
  DIME gift offers (draft theme only).
  One popup component, several offers. Each offer is a [data-dime-fall-gift] root
  with its own <script type="application/json" data-gift-config>.
    - fall: buy 2 qualifying Fall items -> free beanie or bandana (FALLBUNDLE)
    - bogo: buy 1 battery -> second battery, lower-priced one free (FALLBOGO)
  Discounts are automatic; this script only adds the chosen item and verifies
  the discount applied. previewOnly: true never touches the real cart.
*/
(function (scope) {
  "use strict";

  const LINE_KEY = "_dime_gift_offer";
  const LEGACY_KEY = "_dime_fall_gift";

  function markerOf(config) {
    return config.marker || "fall-gift-v1";
  }

  function managed(line, config) {
    const props = line && line.properties;
    if (!props) return false;
    const marker = markerOf(config);
    return props[LINE_KEY] === marker || props[LEGACY_KEY] === marker;
  }

  function giftIds(config) {
    const ids = config.giftProductIds || (config.gifts || []).map((gift) => gift.productId);
    return new Set(ids.map(String));
  }

  function evaluate(cart, config) {
    const qualifying = new Set((config.qualifyingProductIds || []).map(String));
    const gifts = giftIds(config);
    const threshold = Number(config.threshold) || 2;
    const fulfilledAt = Number(config.fulfilledAt) || 0;
    const requireOther = config.requireOtherUnits !== false;
    const items = (cart && cart.items) || [];
    const distinct = new Set();
    let units = 0;
    let otherUnits = 0;
    let minPaidPrice = null;

    for (const item of items) {
      if (managed(item, config) || item.selling_plan_allocation) continue;
      const id = String(item.product_id);
      if (!qualifying.has(id)) continue;
      if (config.excludeGiftProducts && gifts.has(id)) continue;
      const quantity = Math.max(0, Number(item.quantity) || 0);
      if (!quantity) continue;
      units += quantity;
      if (!gifts.has(id)) otherUnits += quantity;
      distinct.add(id);
      const price = Number(item.original_price != null ? item.original_price : item.price);
      if (!Number.isNaN(price) && (minPaidPrice === null || price < minPaidPrice)) minPaidPrice = price;
    }

    const count = config.distinctProducts ? distinct.size : units;
    const fulfilled = fulfilledAt > 0 && count >= fulfilledAt;
    const eligible = count >= threshold && (!requireOther || otherUnits > 0) && !fulfilled;
    const remaining = Math.max(requireOther && !otherUnits ? 1 : 0, threshold - count);

    return {
      count,
      otherUnits,
      eligible,
      fulfilled,
      remaining,
      minPaidPrice,
      managed: items.filter((line) => managed(line, config)),
    };
  }

  function codes(cart) {
    const applications = [
      ...(cart.cart_level_discount_applications || []),
      ...(cart.items || []).flatMap((line) =>
        (line.line_level_discount_allocations || []).map((allocation) => allocation.discount_application)
      ),
    ];
    return [
      ...new Set(
        [
          ...(cart.discount_codes || []).map((discount) => discount.code),
          ...applications.filter((app) => app && app.type === "discount_code").map((app) => app.title),
        ].filter(Boolean)
      ),
    ];
  }

  function discountApplied(cart, title) {
    if (!title || !cart) return false;
    const wanted = String(title).toLowerCase();
    const applications = [
      ...(cart.cart_level_discount_applications || []),
      ...(cart.items || []).flatMap((line) =>
        (line.line_level_discount_allocations || [])
          .filter((allocation) => Number(allocation.amount) > 0)
          .map((allocation) => allocation.discount_application)
      ),
    ];
    return applications.some((app) => app && String(app.title || "").toLowerCase() === wanted);
  }

  function inWindow(config, now, previewAllowed) {
    if (previewAllowed) return true;
    const time = typeof now === "number" ? now : Date.now();
    if (config.startsAt && time < Date.parse(config.startsAt)) return false;
    if (config.endsAt && time >= Date.parse(config.endsAt)) return false;
    return true;
  }

  async function addNativeGift(request, config, gift, variant, operation) {
    const msg = config.messages || {};
    const initial = await request("cart.js");
    if (!evaluate(initial, config).eligible) {
      throw new Error(msg.errRules || "Your cart does not qualify for this offer yet.");
    }
    if (initial.items.some((line) => managed(line, config))) {
      throw new Error(msg.errAlready || "Your cart already has this gift.");
    }
    if (!variant.available) {
      throw new Error(msg.errUnavailable || "This gift is not currently available.");
    }

    const discountCodes = config.discountCodes || {};
    const code = discountCodes[String(gift.productId)] || "";
    const previousCodes = codes(initial);
    const ourCodes = new Set(Object.values(discountCodes));
    const otherCodes = previousCodes.filter((existing) => !ourCodes.has(existing));
    let attemptedAdd = false;
    let attemptedDiscount = false;

    try {
      attemptedAdd = true;
      const properties = {};
      properties[LINE_KEY] = markerOf(config);
      properties._dime_gift_operation = operation;
      properties.Promotion = config.promotion || "Free gift";
      await request("cart/add.js", { items: [{ id: variant.id, quantity: 1, properties }] });

      if (code) {
        attemptedDiscount = true;
        await request("cart/update.js", { discount: [...otherCodes, code].join(",") });
      }

      const fresh = await request("cart.js");
      const giftLines = fresh.items.filter(
        (line) => line.properties && line.properties._dime_gift_operation === operation
      );
      const lineOk = giftLines.length === 1 && giftLines[0].quantity === 1;
      const priceOk =
        config.priceMode === "cheapest"
          ? discountApplied(fresh, config.discountTitle)
          : lineOk && giftLines[0].final_line_price === 0;

      if (!evaluate(fresh, config).eligible || !lineOk || !priceOk) {
        throw new Error(msg.errApply || "The free gift could not be applied with this cart or discount combination.");
      }
      return fresh;
    } catch (error) {
      try {
        if (attemptedAdd) {
          const fresh = await request("cart.js");
          const added = fresh.items.filter(
            (line) => line.properties && line.properties._dime_gift_operation === operation
          );
          for (const line of added) await request("cart/change.js", { id: line.key, quantity: 0 });
        }
        if (attemptedDiscount) await request("cart/update.js", { discount: previousCodes.join(",") });
      } catch (rollbackError) {
        throw new Error("We could not finish updating your cart. Review the cart and remove any paid gift before checkout.");
      }
      throw error;
    }
  }

  const rules = { evaluate, managed, markerOf, codes, discountApplied, inWindow, addNativeGift };
  if (typeof module !== "undefined" && module.exports) module.exports = rules;
  scope.DimeFallGiftRules = rules;
})(typeof window !== "undefined" ? window : this);

(function () {
  "use strict";
  if (typeof document === "undefined") return;

  function readPreviewFlags() {
    try {
      const theme = window.Shopify && window.Shopify.theme;
      if (!theme || theme.role === "main") return [];
      const param = new URLSearchParams(window.location.search).get("dfg_preview");
      if (param) window.sessionStorage.setItem("dfg_preview", param);
      const stored = window.sessionStorage.getItem("dfg_preview") || "";
      return stored.split(",").map((flag) => flag.trim()).filter(Boolean);
    } catch (error) {
      return [];
    }
  }

  function start() {
    const rules = window.DimeFallGiftRules;
    if (!rules) return;
    const roots = Array.from(document.querySelectorAll("[data-dime-fall-gift]")).filter(
      (root) => root.querySelector("dialog") && !root.dataset.initialized
    );
    if (!roots.length) return;

    const cartNode = document.getElementById("dime-gift-cart");
    const shared = {
      cart: { items: [] },
      route: "/",
      previewFlags: readPreviewFlags(),
      queue: [],
      request,
      anyDialogOpen,
      enqueue,
      drain,
    };
    try {
      if (cartNode) shared.cart = JSON.parse(cartNode.textContent);
    } catch (error) {
      shared.cart = { items: [] };
    }

    async function request(path, body) {
      const response = await fetch(
        shared.route + path,
        body
          ? {
              method: "POST",
              headers: { "Content-Type": "application/json", Accept: "application/json" },
              body: JSON.stringify(body),
            }
          : { headers: { Accept: "application/json" }, cache: "no-store" }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || "Please try again.");
      return data;
    }

    function anyDialogOpen() {
      return !!document.querySelector("[data-dime-fall-gift] dialog[open]");
    }

    function enqueue(fn) {
      if (!shared.queue.includes(fn)) shared.queue.push(fn);
    }

    function drain() {
      setTimeout(() => {
        while (shared.queue.length && !anyDialogOpen()) shared.queue.shift()();
      }, 0);
    }

    const offers = [];
    roots.forEach((root) => {
      root.dataset.initialized = "true";
      const node = root.querySelector("script[data-gift-config]") || document.getElementById("dime-fall-gift-config");
      if (!node) return;
      let config;
      try {
        config = JSON.parse(node.textContent);
      } catch (error) {
        root.hidden = true;
        return;
      }
      if (config.cart && !cartNode) shared.cart = config.cart;
      if (config.root) shared.route = config.root;
      const offer = createOffer(root, config, shared, rules);
      if (offer) offers.push(offer);
    });
    if (!offers.length) return;

    const demo = offers.some((offer) => offer.config.demo);
    let sequence = 0;
    let refreshTimer;
    let lastFingerprint = "";
    let cleaning = false;

    function renderAll() {
      offers.forEach((offer) => offer.render());
    }

    async function refresh() {
      if (demo || cleaning || offers.some((offer) => offer.isBusy())) return;
      const ticket = ++sequence;
      try {
        const fresh = await request("cart.js");
        if (ticket !== sequence || offers.some((offer) => offer.isBusy())) return;
        shared.cart = fresh;
        cleaning = true;
        let changed = false;
        for (const offer of offers) {
          if (await offer.cleanup()) changed = true;
        }
        cleaning = false;
        if (changed) {
          window.location.reload();
          return;
        }
        renderAll();
      } catch (error) {
        cleaning = false;
        offers.forEach((offer) => offer.showError("Could not check your cart. Please refresh and try again."));
      }
    }

    if (demo) {
      document.addEventListener("dime:demo-cart", (event) => {
        shared.cart = event.detail;
        renderAll();
      });
      renderAll();
      return;
    }

    const drawer = document.querySelector("cart-drawer");
    if (drawer) {
      new MutationObserver(() => {
        offers.forEach((offer) => offer.mount());
        const items = drawer.querySelector("#CartDrawer-CartItems");
        const fingerprint = items
          ? items.textContent + Array.from(items.querySelectorAll("input")).map((input) => input.value).join(",")
          : "";
        if (fingerprint !== lastFingerprint) {
          lastFingerprint = fingerprint;
          clearTimeout(refreshTimer);
          refreshTimer = setTimeout(refresh, 650);
        }
      }).observe(drawer, { childList: true, subtree: true, attributes: true, attributeFilter: ["value"] });
    }
    const cartPage = document.querySelector("cart-items");
    if (cartPage) {
      new MutationObserver(() => {
        clearTimeout(refreshTimer);
        refreshTimer = setTimeout(refresh, 650);
      }).observe(cartPage, { childList: true, subtree: true });
    }
    window.addEventListener("pageshow", refresh);
    window.addEventListener("focus", refresh);
    document.addEventListener("dime:cart-updated", refresh);
    refresh();
  }

  function createOffer(root, config, shared, rules) {
    const msg = config.messages || {};
    config.giftProductIds = (config.gifts || []).map((gift) => gift.productId);
    config.gifts = (config.gifts || [])
      .map((gift) => ({ ...gift, variants: (gift.variants || []).filter((variant) => variant.available) }))
      .filter((gift) => gift.variants.length);

    const previewAllowed = shared.previewFlags.includes(config.previewParam || config.id || "");
    if (!config.enabled || !config.gifts.length || !rules.inWindow(config, Date.now(), previewAllowed)) {
      root.hidden = true;
      return null;
    }

    const dialog = root.querySelector("dialog");
    const cards = root.querySelector("[data-gift-cards]");
    const status = root.querySelector("[data-gift-status]");
    const banner = root.querySelector("[data-gift-banner]");
    const launcher = root.querySelector("[data-gift-open]");
    const receipt = root.querySelector("[data-gift-receipt]");
    const bannerBox = root.querySelector(".dfg-banner");
    const draftLabel = root.querySelector("[data-draft-label]");
    const bannerMount = document.createElement("div");
    bannerMount.setAttribute("data-dime-fall-gift", "banner");
    if (config.id) bannerMount.setAttribute("data-offer", config.id);

    const cardRefs = [];
    let previewGift = null;
    let dismissed = false;
    let busy = false;
    let previousFocus;

    const money = (cents) =>
      new Intl.NumberFormat(config.locale || "en-US", { style: "currency", currency: config.currency || "USD" }).format(
        cents / 100
      );
    const text = (tag, value, className) => {
      const el = document.createElement(tag);
      el.textContent = value;
      if (className) el.className = className;
      return el;
    };
    const fill = (template, values) =>
      String(template || "").replace(/\{(\w+)\}/g, (match, key) => (key in values ? values[key] : match));

    function setBusy(value) {
      busy = value;
      dialog.setAttribute("aria-busy", String(value));
      root.querySelectorAll("[data-gift-add]").forEach((button) => {
        button.disabled = value || button.dataset.soldOut === "true";
      });
      root.querySelectorAll("[data-gift-close]").forEach((button) => {
        button.disabled = value;
      });
    }

    function close() {
      if (busy) return;
      dismissed = true;
      dialog.close();
    }

    function open() {
      const result = rules.evaluate(shared.cart, config);
      if (!result.eligible || previewGift || result.managed.length || busy || dialog.open) return;
      if (shared.anyDialogOpen()) {
        shared.enqueue(open);
        return;
      }
      status.textContent = "";
      previousFocus = document.activeElement;
      const drawer = document.querySelector("cart-drawer.active");
      if (drawer && typeof drawer.close === "function") drawer.close();
      if (typeof window.removeTrapFocus === "function") window.removeTrapFocus();
      updatePrices();
      dialog.showModal();
    }

    function mount() {
      if (config.demo) return true;
      const slot = document.querySelector("cart-drawer .drawer__footer") || document.querySelector("#main-cart-footer");
      if (!slot) return false;
      if (bannerMount.parentElement !== slot) {
        bannerMount.appendChild(bannerBox);
        slot.prepend(bannerMount);
      }
      return true;
    }

    function updatePrices() {
      if (config.priceMode !== "cheapest") return;
      const paid = rules.evaluate(shared.cart, config).minPaidPrice;
      cardRefs.forEach((ref) => {
        const variant = ref.gift.variants.find((v) => String(v.id) === ref.select.value) || ref.gift.variants[0];
        const price = Number(variant && variant.price != null ? variant.price : ref.gift.price);
        ref.prices.replaceChildren();
        if (paid === null || price <= paid) {
          ref.prices.appendChild(text("s", money(price)));
          ref.prices.appendChild(text("strong", "FREE"));
        } else {
          ref.prices.appendChild(text("span", money(price)));
          ref.prices.appendChild(text("strong", fill(msg.saveLabel || "SAVE {amount}", { amount: money(paid) })));
        }
      });
    }

    function syncImages() {
      cardRefs.forEach((ref) => {
        const variant = ref.gift.variants.find((v) => String(v.id) === ref.select.value) || ref.gift.variants[0];
        const src = (variant && variant.image) || ref.gift.image;
        if (src && ref.image.getAttribute("src") !== src) ref.image.src = src;
      });
    }

    function renderReceipt(selected) {
      const product = config.gifts.find((gift) => String(gift.productId) === String(selected.product_id));
      const photo = selected.image || (product && product.image);
      if (photo) {
        const image = document.createElement("img");
        image.src = photo;
        image.alt = selected.product_title || selected.title || "";
        image.width = 88;
        image.height = 104;
        image.className = "dfg-cart-image";
        receipt.appendChild(image);
      }
      const details = text("div", "", "dfg-cart-details");
      details.appendChild(text("strong", selected.product_title || selected.title || ""));
      details.appendChild(text("strong", fill(msg.receiptPrice || "Free gift — {zero}", { zero: money(0) }), "dfg-cart-price"));
      details.appendChild(text("span", "Quantity: 1"));
      if (config.previewOnly) details.appendChild(text("span", "Draft preview", "dfg-preview-note"));
      receipt.appendChild(details);
      if (config.previewOnly) {
        const change = text("button", msg.change || "Change gift", "dfg-text-button");
        change.type = "button";
        change.addEventListener("click", () => {
          previewGift = null;
          dismissed = true;
          render();
          open();
        });
        details.appendChild(change);
      }
    }

    function render() {
      const hasSlot = mount();
      const result = rules.evaluate(shared.cart, config);
      if (!result.eligible) {
        previewGift = null;
        dismissed = false;
        if (dialog.open && !busy) dialog.close();
      }
      const selected = previewGift || result.managed[0] || null;

      let message = "";
      if (selected) message = msg.bannerSelected || "";
      else if (result.fulfilled) message = msg.bannerFulfilled || "";
      else if (result.eligible) message = msg.bannerReady || "";
      else if (result.count >= 1 && config.requireOtherUnits !== false && !result.otherUnits) message = msg.bannerNeedOther || "";
      else if (result.count >= 1)
        message = fill(msg.bannerProgress, {
          remaining: result.remaining,
          items: result.remaining === 1 ? "item" : "items",
        });

      banner.textContent = message;
      const show = !!selected || (result.count > 0 && !!message);
      bannerBox.hidden = !(hasSlot && show);
      launcher.hidden = !result.eligible || !!selected;
      banner.hidden = !!selected;
      bannerBox.classList.toggle("dfg-has-gift", !!selected);
      receipt.hidden = !selected;
      receipt.replaceChildren();
      if (selected) renderReceipt(selected);
      updatePrices();

      if (result.eligible && !selected && !dismissed && !busy) open();
    }

    async function addGift(gift, variant) {
      if (busy) return;
      setBusy(true);
      status.textContent = msg.checking || "Checking your gift…";
      try {
        if (!config.demo) shared.cart = await shared.request("cart.js");
        const result = rules.evaluate(shared.cart, config);
        if (!result.eligible) throw new Error(msg.errNotEligible || "Your cart does not qualify for this offer yet.");
        if (result.managed.length || previewGift) throw new Error(msg.errAlready || "Your cart already has this gift.");
        if (config.previewOnly) {
          previewGift = {
            product_id: gift.productId,
            image: variant.image || gift.image,
            product_title: gift.title + (variant.title === "Default Title" ? "" : " — " + variant.title),
          };
          setBusy(false);
          dialog.close();
          render();
          return;
        }
        if (!config.checkoutReady) throw new Error(msg.errDraft || "This promotion is still a draft.");
        const operation =
          window.crypto && typeof window.crypto.randomUUID === "function"
            ? window.crypto.randomUUID()
            : Date.now() + "-" + Math.random().toString(16).slice(2);
        shared.cart = await rules.addNativeGift(shared.request, config, gift, variant, operation);
        window.location.assign((config.root || "/") + "cart");
      } catch (error) {
        dismissed = true;
        status.textContent = (error && error.message) || "Could not add your gift. Please try again.";
        setBusy(false);
        render();
      }
    }

    config.gifts.forEach((gift) => {
      const article = document.createElement("article");
      article.className = "dfg-card";
      const image = document.createElement("img");
      image.src = gift.image;
      image.alt = gift.title;
      image.width = 240;
      image.height = 220;
      image.loading = "lazy";
      article.appendChild(image);
      article.appendChild(text("h3", gift.title));

      const prices = document.createElement("p");
      prices.className = "dfg-prices";
      prices.appendChild(text("s", money(gift.price)));
      prices.appendChild(text("strong", "FREE"));
      article.appendChild(prices);

      const available = gift.variants.filter((variant) => variant.available);
      const select = document.createElement("select");
      select.setAttribute("aria-label", fill(msg.selectLabel || "Choose {title} option", { title: gift.title }));
      gift.variants.forEach((variant) => {
        const option = document.createElement("option");
        option.value = String(variant.id);
        option.textContent =
          (variant.title === "Default Title" ? "One size" : variant.title) + (variant.available ? "" : " — Sold out");
        option.disabled = !variant.available;
        select.appendChild(option);
      });
      if (available.length) select.value = String(available[0].id);
      select.hidden = gift.variants.length === 1;
      select.addEventListener("change", () => {
        syncImages();
        updatePrices();
      });
      article.appendChild(select);

      const button = text("button", available.length ? msg.choose || "Choose this gift" : "Sold out", "dfg-select");
      button.type = "button";
      button.dataset.giftAdd = "";
      button.dataset.soldOut = String(!available.length);
      button.disabled = !available.length;
      button.addEventListener("click", () => {
        const variant = gift.variants.find((v) => String(v.id) === select.value && v.available);
        if (variant) addGift(gift, variant);
      });
      article.appendChild(button);
      cards.appendChild(article);
      cardRefs.push({ gift, select, prices, image });
    });

    syncImages();
    if (draftLabel) draftLabel.hidden = !config.previewOnly;
    launcher.addEventListener("click", open);
    root.querySelectorAll("[data-gift-close]").forEach((button) => button.addEventListener("click", close));
    dialog.addEventListener("cancel", (event) => {
      event.preventDefault();
      close();
    });
    dialog.addEventListener("click", (event) => {
      if (event.target !== dialog) return;
      const box = dialog.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) {
        close();
      }
    });
    dialog.addEventListener("close", () => {
      if (previousFocus && previousFocus.isConnected) previousFocus.focus();
      shared.drain();
    });

    return {
      config,
      render,
      mount,
      isBusy: () => busy,
      showError(message) {
        status.textContent = message;
        setBusy(false);
      },
      async cleanup() {
        if (config.previewOnly) return false;
        const result = rules.evaluate(shared.cart, config);
        if (result.eligible || !result.managed.length) return false;
        setBusy(true);
        for (const item of result.managed) await shared.request("cart/change.js", { id: item.key, quantity: 0 });
        return true;
      },
    };
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start, { once: true });
  else start();
})();
